import { Vector3 } from 'three';
import { Table } from '../engine/table.js';
import { Rack } from '../engine/rack.js';
import { Ball, State } from '../engine/ball.js';
import { cueStrike } from '../engine/physics.js';
import {
  R,
  muS,
  mu,
  g,
  setmu,
  setmuS,
  setmuC,
} from '../engine/constants.js';
import { TableGeometry } from '../engine/tablegeometry.js';
import { PocketGeometry } from '../engine/pocketgeometry.js';
import { activeResolved, seatResolvedBalls } from './rack-layout.js';
import type { ShotObservation } from './rules/types.js';

/**
 * 物理エンジン（tailuge 由来・src/engine）と描画・入力をつなぐ型付きアダプタ。
 * 描画レイヤはこのファイル経由でのみエンジンに触れる（結合の局所化）。
 */

/** 球半径 [m]（エンジン定数）。 */
export const ballRadius: number = R;

/** クッションのレールセグメント（ナックル間）。両端 (x,y) と向き。 */
export interface RailSegment {
  readonly axis: 'x' | 'y';
  /** セグメント中心の固定座標（axis='x' なら y、'y' なら x）。 */
  readonly fixed: number;
  /** 可変座標の範囲 [min, max]。 */
  readonly from: number;
  readonly to: number;
}

/** 描画に必要なテーブル幾何（読み取り専用スナップショット）。 */
export interface TableLayout {
  /** 長辺方向（x）のクッション半幅 [m]。 */
  readonly halfX: number;
  /** 短辺方向（y）のクッション半幅 [m]。 */
  readonly halfY: number;
  /** ポケット中心と捕捉半径。 */
  readonly pockets: ReadonlyArray<{ x: number; y: number; radius: number }>;
  /** ナックル間のレールセグメント（ポケット開口を除く）。 */
  readonly rails: ReadonlyArray<RailSegment>;
}

interface Knuckle {
  pos: Vector3;
}
interface PocketTriple {
  knuckleNE?: Knuckle;
  knuckleNW?: Knuckle;
  knuckleSE?: Knuckle;
  knuckleSW?: Knuckle;
}

/** ナックル列（クッション端の位置）を時計回り（南西→北→東→南→南西）で返す。
 *  レール内縁を構築する際に使用。 */
export const getKnuckleOrder = (): { x: number; y: number }[] => {
  const pk = PocketGeometry.pockets as Record<string, PocketTriple>;
  const order = [
    pk.pocketSW?.knuckleNW,
    pk.pocketNW?.knuckleSW,
    pk.pocketNW?.knuckleNE,
    pk.pocketN?.knuckleNW,
    pk.pocketN?.knuckleNE,
    pk.pocketNE?.knuckleNW,
    pk.pocketNE?.knuckleSE,
    pk.pocketSE?.knuckleNE,
    pk.pocketSE?.knuckleSW,
    pk.pocketS?.knuckleSE,
    pk.pocketS?.knuckleSW,
    pk.pocketSW?.knuckleSE,
  ];
  return order
    .filter((k): k is Knuckle => !!k)
    .map((k) => ({ x: k.pos.x, y: k.pos.y }));
};

export const getTableLayout = (): TableLayout => {
  const tx = TableGeometry.tableX;
  const ty = TableGeometry.tableY;
  const pk = PocketGeometry.pockets as Record<string, PocketTriple>;

  const railX = (fixed: number, a?: Knuckle, b?: Knuckle): RailSegment => ({
    axis: 'x',
    fixed,
    from: Math.min(a!.pos.x, b!.pos.x),
    to: Math.max(a!.pos.x, b!.pos.x),
  });
  const railY = (fixed: number, a?: Knuckle, b?: Knuckle): RailSegment => ({
    axis: 'y',
    fixed,
    from: Math.min(a!.pos.y, b!.pos.y),
    to: Math.max(a!.pos.y, b!.pos.y),
  });

  // 長辺（y=±ty）はサイドポケットで2分割、短辺（x=±tx）は1本。
  const rails: RailSegment[] = [
    railX(ty, pk.pocketNW?.knuckleNE, pk.pocketN?.knuckleNW),
    railX(ty, pk.pocketN?.knuckleNE, pk.pocketNE?.knuckleNW),
    railX(-ty, pk.pocketSW?.knuckleSE, pk.pocketS?.knuckleSW),
    railX(-ty, pk.pocketS?.knuckleSE, pk.pocketSE?.knuckleSW),
    railY(-tx, pk.pocketNW?.knuckleSW, pk.pocketSW?.knuckleNW),
    railY(tx, pk.pocketNE?.knuckleSE, pk.pocketSE?.knuckleNE),
  ];

  return {
    halfX: tx,
    halfY: ty,
    pockets: (
      PocketGeometry.pocketCenters as Array<{ pos: Vector3; radius: number }>
    ).map((p) => ({ x: p.pos.x, y: p.pos.y, radius: p.radius })),
    rails,
  };
};

/** オートエイム用のポケット狙点＋ジョー（ナックル）。ジョーは球が `R + r` 未満まで
 *  近づくと弾かれる（Knuckle.willBounce と同条件）ため、進入経路がジョーをクリアするか
 *  の判定に使う（サイドポケットの角弾き防止）。 */
export interface PocketAim {
  /** ポケット中心（狙点）。 */
  readonly x: number;
  readonly y: number;
  /** サイドポケット（長辺中央）か。近距離時にコーナーより優先する。 */
  readonly isSide: boolean;
  /** 開口を挟む2つのナックル（中心 + 半径）。 */
  readonly jaws: ReadonlyArray<{ x: number; y: number; r: number }>;
}

/** 各ポケットの狙点とジョー（開口を挟む2ナックル）を返す。 */
export const getPocketAims = (): PocketAim[] => {
  interface K {
    pos: Vector3;
    radius: number;
  }
  interface P {
    pocket: { pos: Vector3 };
    [k: string]: unknown;
  }
  const pk = PocketGeometry.pockets as Record<string, P>;
  // [ポケットキー, [ジョー2つ], サイドか]。N/S が長辺中央＝サイドポケット。
  const defs: ReadonlyArray<readonly [string, readonly [string, string], boolean]> = [
    ['pocketNW', ['knuckleNE', 'knuckleSW'], false],
    ['pocketNE', ['knuckleNW', 'knuckleSE'], false],
    ['pocketSW', ['knuckleSE', 'knuckleNW'], false],
    ['pocketSE', ['knuckleNE', 'knuckleSW'], false],
    ['pocketN', ['knuckleNW', 'knuckleNE'], true],
    ['pocketS', ['knuckleSW', 'knuckleSE'], true],
  ];
  const out: PocketAim[] = [];
  for (const [key, kk, isSide] of defs) {
    const p = pk[key];
    if (!p) continue;
    const jaws: { x: number; y: number; r: number }[] = [];
    for (const name of kk) {
      const k = p[name] as K | undefined;
      if (k && k.pos) jaws.push({ x: k.pos.x, y: k.pos.y, r: k.radius });
    }
    out.push({ x: p.pocket.pos.x, y: p.pocket.pos.y, isSide, jaws });
  }
  return out;
};

/** ヘッドストリングの x 座標（ブレイク手球位置＝キッチン境界。ヘッド側＝x<これ）。
 *  ブレイク時の手球は x=-tableX/2 に置かれ、これがヘッドストリング（ヘッド端から1/4）。 */
export const headStringX = (): number => -TableGeometry.tableX / 2;

/** 現在ヘッドストリングより手前（キッチン側）にいる的玉 id（id>0・盤上）。
 *  ブレイク中に毎フレーム呼び、和集合を取ることで「越えて戻った球」も拾える（3ポイント用）。 */
export const objectBallsBehindHeadString = (table: Table): number[] => {
  const hx = headStringX();
  const out: number[] = [];
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    if (b.pos.x < hx) out.push(b.id);
  }
  return out;
};

/** 手球の現在位置（照準UI用）。 */
export const cueBallPos = (table: Table): { x: number; y: number } => ({
  x: table.cueball.pos.x,
  y: table.cueball.pos.y,
});

/** 描画用の球スナップショット。 */
export interface BallSnapshot {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly wx: number;
  readonly wy: number;
  readonly wz: number;
  readonly onTable: boolean;
}

/** プレイモード。第一弾は初期配置（球数・ラック形状）のみ。ルール判定は未実装。 */
export type PlayMode = 'practice' | 'nineBall' | 'tenBall' | 'eightBall';

/**
 * ブレイク既定の手球位置（システム既定値）。
 * - 9ボール: **キッチンライン直近のサイドブレイク**。
 *   x: ヘッドストリング（キッチン線・x=-tableX/2）に球エッジが接する位置＝球中心 `-tableX/2 - R`
 *      （キッチン側 R だけ内側・ヘッドストリングを跨がず合法）。
 *   y: 長クッション（-y 側のレール）から手球1.5個分（=3R）の隙間＝球中心 `-tableY + 4R`。
 *   プロのスナップブレイク（ライン直近のサイドブレイク）相当のポジショニング。
 * - 他モード: センター（ヘッドスポット x=-tableX/2・y=0）。
 * いずれもキッチン内なので、配置モードでプレイヤーが微調整できる。
 */
const systemDefaultBreakCuePos = (mode: PlayMode): Vector3 => {
  if (mode === 'nineBall') {
    // x: キッチンライン（x=-tableX/2）に球エッジが接する位置＝球中心 -tableX/2 - R。
    const xSnap = -TableGeometry.tableX / 2 - R;
    // y: 左長クッション（y=-tableY）から球エッジまで 1.5 球分 = 3R の隙間。
    // ⇒ 球中心 y = -tableY + R + 3R = -tableY + 4R。
    const ySnap = -TableGeometry.tableY + 4 * R;
    return new Vector3(xSnap, ySnap, 0);
  }
  return new Vector3(-TableGeometry.tableX / 2, 0, 0); // センター（ヘッドスポット）
};

// ---- ユーザーカスタム ブレイク手玉位置（localStorage 永続） ----
// ブレイク前にユーザーが手玉を動かしたら、その座標を `pool.breakCuePos.<mode>` に保存する。
// 次回ラック開始時・「リセット」ボタン押下時の両方で復元される（お気に入り位置の継続使用）。
// 範囲外（キッチン外・盤外）の保存値は無視してシステム既定にフォールバックする。
const BREAK_CUE_POS_KEY_PREFIX = 'pool.breakCuePos.';
const loadStoredBreakCuePos = (
  mode: PlayMode,
): { x: number; y: number } | null => {
  if (mode === 'practice') return null;
  if (typeof localStorage === 'undefined') return null; // Node.js テスト環境
  try {
    const raw = localStorage.getItem(BREAK_CUE_POS_KEY_PREFIX + mode);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const rec = parsed as { x?: unknown; y?: unknown };
    const x = rec.x;
    const y = rec.y;
    if (typeof x !== 'number' || typeof y !== 'number') return null;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    // 範囲チェック: 盤内（球中心が盤端から R 内側）のみ。
    // ヘッドストリング制約は適用しない（UI で配置できた位置はそのまま許容＝ユーザー意図を尊重）。
    const tx = TableGeometry.tableX;
    const ty = TableGeometry.tableY;
    if (Math.abs(x) > tx - R) return null; // 長辺方向のクッション越え
    if (Math.abs(y) > ty - R) return null; // 短辺方向のクッション越え
    return { x, y };
  } catch (err) {
    console.error('breakCuePos: localStorage 読み出し失敗:', err);
    return null;
  }
};
/** ユーザーカスタム値があればそれを、なければシステム既定値を返す。 */
const breakCuePos = (mode: PlayMode): Vector3 => {
  const stored = loadStoredBreakCuePos(mode);
  if (stored) return new Vector3(stored.x, stored.y, 0);
  return systemDefaultBreakCuePos(mode);
};
/**
 * ブレイク手玉位置をユーザーカスタムとして保存する（controls.ts がブレイクショット時に呼ぶ）。
 * 範囲外の値は保存しない（ガード）。プラクティスは対象外。
 */
export const saveBreakCuePos = (mode: PlayMode, x: number, y: number): void => {
  if (mode === 'practice') return;
  if (typeof localStorage === 'undefined') return; // Node.js テスト環境
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;
  // 保存時の範囲チェック: 盤内のみ（ヘッドストリング制約は外す）。
  // UI で配置できた位置はユーザー意図としてそのまま記憶する。
  const tx = TableGeometry.tableX;
  const ty = TableGeometry.tableY;
  if (Math.abs(x) > tx - R) return;
  if (Math.abs(y) > ty - R) return;
  try {
    localStorage.setItem(
      BREAK_CUE_POS_KEY_PREFIX + mode,
      JSON.stringify({ x, y }),
    );
  } catch (err) {
    console.error('breakCuePos: localStorage 保存失敗:', err);
  }
};
/**
 * ブレイク手玉位置のユーザーカスタム値を削除する（設定リセットから呼ぶ）。
 * mode 指定なしで全モード分削除する。
 */
export const clearBreakCuePos = (mode?: PlayMode): void => {
  if (typeof localStorage === 'undefined') return; // Node.js テスト環境
  try {
    if (mode) {
      localStorage.removeItem(BREAK_CUE_POS_KEY_PREFIX + mode);
    } else {
      for (const m of ['nineBall', 'tenBall', 'eightBall'] as const) {
        localStorage.removeItem(BREAK_CUE_POS_KEY_PREFIX + m);
      }
    }
  } catch (err) {
    console.error('breakCuePos: localStorage 削除失敗:', err);
  }
};
/** localStorage に保存された全 breakCuePos キーを列挙（設定リセット UI の情報表示用）。 */
export const breakCuePosStorageKeys = (): string[] => {
  return (['nineBall', 'tenBall', 'eightBall'] as const).map(
    (m) => BREAK_CUE_POS_KEY_PREFIX + m,
  );
};

/**
 * モード別ラック生成。
 * プラクティスは engine Rack（手球＋1番）をそのまま使う。
 * それ以外は自前のラックレイアウト（決定論的タイト基準＋保存オフセット）で的玉を配置し、
 * 手球はモード別ブレイク既定位置へ置く。ボール id=番号 を固定（色/番号の一貫性のため）。
 */
// ---- ラックの微小ジッタ（実機ラックの個体差モデル） ----
// ビリヤードのブレイクはカオス的で、わずか ~0.1mm の初期差で散らばりが一変する。
// 見た目の配置（ユーザーのラック調整）は保ったまま、毎回のブレイクに自然なばらつきを与える。
// OFF にすると完全に再現可能（決定論）な配置になる。
const RACK_JITTER_M = 0.0002; // ±0.2mm
let rackSpreadEnabled = true;
/** ラックの微小ジッタ（ばらつき）の有効/無効を切り替える（設定から呼ぶ）。 */
export const setRackSpread = (enabled: boolean): void => {
  rackSpreadEnabled = enabled;
};
const jitter = (): number =>
  rackSpreadEnabled ? (Math.random() * 2 - 1) * RACK_JITTER_M : 0;

const rackFor = (mode: PlayMode): InstanceType<typeof Ball>[] => {
  if (mode === 'practice') {
    Ball.id = 0;
    return Rack.practice();
  }
  // 的玉配置 = アクティブタブの解決済み（番号入替＋位置オフセット込み・rack-layout）。
  // 1) ジッタ（個体差）を加え → 2) 重なりを解消（エディタ残り・ジッタ由来の重なりをゼロ保証）。
  //    エンジンへ重なった初期配置を渡すと、分離インパルスでの球の暴発・NaN 発散（フリーズ）を招くため。
  const jittered = activeResolved(mode).map((b) => ({
    number: b.number,
    x: b.x + jitter(),
    y: b.y + jitter(),
  }));
  const resolved = seatResolvedBalls(jittered);
  Ball.id = 0;
  const cue = new Ball(breakCuePos(mode), 0xffffff, 0);
  const balls: InstanceType<typeof Ball>[] = [cue];
  for (const b of resolved) {
    Ball.id = b.number; // 生成する Ball の .id を番号に固定
    balls.push(new Ball(new Vector3(b.x, b.y, 0), 0, b.number));
  }
  return balls;
};

/** ヘッドスポット位置（手球初期位置・スクラッチ復帰先）。 */
const headSpot = (): Vector3 =>
  new Vector3(-TableGeometry.tableX / 2, 0, 0);

/** フットスポット位置（ラック頂点位置）。 */
const footSpot = (): Vector3 =>
  new Vector3(TableGeometry.tableX / 2, 0, 0);

/**
 * 手球を盤上に復帰させる（スクラッチ後）。
 *   1) ヘッドスポット → 他球と被らなければそこに置く
 *   2) フットスポット → 同様
 *   3) 中央 → 同様
 *   4) ヘッドスポット周辺を 2R 刻みでスキャン
 * いずれかで配置に成功したら true を返す。
 */
export const respotCueBall = (table: Table): boolean => {
  const cb = table.cueball;
  const halfX = TableGeometry.tableX;
  const halfY = TableGeometry.tableY;
  const candidates: Vector3[] = [headSpot(), footSpot(), new Vector3(0, 0, 0)];

  const noCollision = (p: Vector3): boolean => {
    for (const b of table.balls) {
      if (b.id === 0 || !b.onTable()) continue;
      if (b.pos.distanceTo(p) < 2 * R + 0.001) return false;
    }
    return true;
  };

  const place = (p: Vector3): void => {
    cb.pos.copy(p);
    cb.vel.set(0, 0, 0);
    cb.rvel.set(0, 0, 0);
    cb.state = State.Stationary;
  };

  for (const c of candidates) {
    if (noCollision(c)) {
      place(c);
      return true;
    }
  }
  // ヘッドスポット周辺をスキャンしてフォールバック位置を探す。
  const probe = new Vector3();
  for (let dx = -0.3; dx <= 0.3; dx += 2 * R) {
    for (let dy = -0.3; dy <= 0.3; dy += 2 * R) {
      probe.set(-halfX / 2 + dx, dy, 0);
      if (Math.abs(probe.x) > halfX - R) continue;
      if (Math.abs(probe.y) > halfY - R) continue;
      if (noCollision(probe)) {
        place(probe);
        return true;
      }
    }
  }
  return false;
};

/** 現在 手球が盤上にいないか（落下中／落下済）を判定。 */
export const isCueBallScratched = (table: Table): boolean =>
  !table.cueball.onTable();

/**
 * 衝突ペアの解決順をシャッフルする（ブレイクの対称性アーティファクト解消）。
 *
 * エンジンの `advance()` は衝突ペアを `pairs` 配列の順で逐次解決する。完全整列のタイトラックを
 * ど真ん中ヘッドオンで撞くと、同時多体接触が**固定順**で解かれるため、特定球（例: 2番が前方の
 * 4番・9番へ対称フォークして止まる）が毎回ほぼ動かない**非物理的な決定パターン**になる。
 * 同時接触の解決順は物理的に未定義なので、順序をランダム化するのが中立かつ自然。
 * 自前境界（world）でエンジンの公開フィールド `pairs` を入れ替えるのみ（GPL エンジンは不改変）。
 * ジッタ無効（決定論モード）では固定順を保ち再現性を維持する。
 */
const shufflePairs = (table: Table): void => {
  if (!rackSpreadEnabled) return; // 決定論モードは固定順（再現性維持）
  const pairs = table.pairs;
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = pairs[i]!;
    pairs[i] = pairs[j]!;
    pairs[j] = tmp;
  }
};

export const createWorld = (mode: PlayMode = 'eightBall'): Table => {
  const table = new Table(rackFor(mode));
  shufflePairs(table);
  return table;
};

export const resetWorld = (table: Table, mode: PlayMode = 'eightBall'): void => {
  table.initialiseBalls(rackFor(mode));
  shufflePairs(table);
};

/** 盤上にいる球の id 一覧（手球 0 を含む）。 */
export const onTableBallIds = (table: Table): number[] =>
  table.balls.filter((b) => b.onTable()).map((b) => b.id);

/** 盤上に残っている的玉（手球 0 を除く）の数。ブレイクラン判定に使う。 */
export const objectBallsRemaining = (table: Table): number =>
  table.balls.filter((b) => b.id !== 0 && b.onTable()).length;

/** 指定位置が盤内かつ他球と重ならないか（id は判定から除外）。 */
export const isFreeSpot = (
  table: Table,
  x: number,
  y: number,
  excludeId: number,
): boolean => {
  const halfX = TableGeometry.tableX;
  const halfY = TableGeometry.tableY;
  if (Math.abs(x) > halfX - R || Math.abs(y) > halfY - R) return false;
  for (const b of table.balls) {
    if (b.id === excludeId || !b.onTable()) continue;
    const dx = b.pos.x - x;
    const dy = b.pos.y - y;
    if (dx * dx + dy * dy < (2 * R) * (2 * R)) return false;
  }
  return true;
};

/** 盤内へクランプした座標を返す（クッション内側 R マージン）。 */
const clampToTable = (x: number, y: number): { x: number; y: number } => {
  const halfX = TableGeometry.tableX;
  const halfY = TableGeometry.tableY;
  return {
    x: Math.max(-(halfX - R), Math.min(halfX - R, x)),
    y: Math.max(-(halfY - R), Math.min(halfY - R, y)),
  };
};

/**
 * 指定 id の球を (x,y) へ移動（盤内クランプ・速度ゼロ）。手球配置・プラクティス再配置用。
 * 他球との重なりを解消する（重なった静止球にショットすると衝突解決が破綻＝Depth exceeded
 * 例外で停止するため）。重なる場合は最も近い「触れる直前」の位置へ押し出す。
 */
export const placeBallAt = (
  table: Table,
  id: number,
  x: number,
  y: number,
): void => {
  const b = table.balls.find((ball) => ball.id === id);
  if (!b) return;
  // 接触ぎりぎり＋わずかな隙間（0.1mm）まで離す。完全フローズン（厳密 2R）による
  // 数値不安定を避けつつ、ダブルヒット判定（隙間≤5mm）は成立する近さを許す。
  const minDist = 2 * R + 0.0001;
  let p = clampToTable(x, y);
  for (let iter = 0; iter < 12; iter++) {
    let moved = false;
    for (const o of table.balls) {
      if (o.id === id || !o.onTable()) continue;
      const dx = p.x - o.pos.x;
      const dy = p.y - o.pos.y;
      const d = Math.hypot(dx, dy);
      if (d < minDist) {
        moved = true;
        if (d < 1e-6) {
          // 中心が完全一致: 任意方向（+x）へ退避。
          p = { x: o.pos.x + minDist, y: o.pos.y };
        } else {
          p = { x: o.pos.x + (dx / d) * minDist, y: o.pos.y + (dy / d) * minDist };
        }
      }
    }
    p = clampToTable(p.x, p.y); // 押し出し後に再度盤内へ収める
    if (!moved) break;
  }
  b.pos.set(p.x, p.y, 0);
  b.vel.set(0, 0, 0);
  b.rvel.set(0, 0, 0);
  b.state = State.Stationary;
};

/** プラクティス: 指定番号の球を追加（空きスポットを探して配置）。既存なら何もしない。
 *  Ball.id は自動インクリメントのため、.id=番号 になるよう生成前に固定する。 */
export const addBall = (table: Table, id: number, x: number, y: number): void => {
  if (table.balls.some((b) => b.id === id && b.onTable())) return;
  const p = clampToTable(x, y);
  Ball.id = id;
  const ball = new Ball(new Vector3(p.x, p.y, 0), 0, id);
  table.initialiseBalls([...table.balls.filter((b) => b.id !== id), ball]);
};

/** プラクティス: 指定 id の球を取り除く（手球 0 は除去不可）。 */
export const removeBall = (table: Table, id: number): void => {
  if (id === 0) return;
  table.initialiseBalls(table.balls.filter((b) => b.id !== id));
};

export interface ShotParams {
  /** 進行方向角 [rad]（+x 基準）。 */
  readonly angle: number;
  /** 力（初速 m/s）。 */
  readonly power: number;
  /** 撞点 横方向 [-0.5..0.5]（R比）。 */
  readonly offsetX: number;
  /** 撞点 縦方向 [-0.5..0.5]（R比）。 */
  readonly offsetY: number;
  /** キュー仰角 [rad]。 */
  readonly elevation: number;
}

/** 手球にショットを与える。全球静止時のみ有効。 */
export const shoot = (table: Table, p: ShotParams): boolean => {
  if (!table.allStationary()) return false;
  const strike = cueStrike(
    p.angle,
    p.power,
    new Vector3(p.offsetX, p.offsetY, 0),
    p.elevation,
  );
  const cue = table.cueball;
  cue.vel.copy(strike.vel);
  cue.rvel.copy(strike.rvel);
  cue.state = State.Sliding;
  return true;
};

/** 固定タイムステップで物理を1ステップ進める。 */
export const step = (table: Table, dt: number): void => {
  table.advance(dt);
};

export const isResting = (table: Table): boolean => table.allStationary();

/** 効果音用イベント（engine の SimOutcome を GPL 非依存の形に写したもの）。 */
export interface ShotEvent {
  readonly type: 'collision' | 'cushion' | 'pot';
  /** 衝突/反発/落下時の速度 [m/s]（音量・ピッチに使う）。 */
  readonly speed: number;
}

/**
 * 直近フレームで発生した物理イベント（球衝突/クッション/ポケット）を取り出す。
 * engine 内部の outcome ログを消費して肥大を防ぐ（GPL 型を境界の外へ漏らさない）。
 */
export const drainEvents = (table: Table): ShotEvent[] =>
  table.drainOutcomes().map((o) => ({ type: o.type, speed: o.speed }));

/** 手球の現在の水平速度 [m/s]（ショット音の強さに使う）。 */
export const cueBallSpeed = (table: Table): number =>
  Math.hypot(table.cueball.vel.x, table.cueball.vel.y);

/** 全球を即時静止させる（収束しない場合の安全網）。 */
export const forceSettle = (table: Table): void => {
  table.halt();
};

// ---- ショット・アンドゥ（撞く直前の盤面スナップショット） ----
interface SavedBall {
  id: number;
  x: number;
  y: number;
  z: number;
  state: State;
}
let savedShot: SavedBall[] | null = null;

/** 撞く直前の全球状態を保存する。 */
export const saveShotState = (table: Table): void => {
  savedShot = table.balls.map((b) => ({
    id: b.id,
    x: b.pos.x,
    y: b.pos.y,
    z: b.pos.z,
    state: b.state,
  }));
};

/** 保存済みスナップショットがあるか。 */
export const hasShotState = (): boolean => savedShot !== null;

/** スナップショットを破棄する（リセット/再ラック/球数変更時）。 */
export const clearShotState = (): void => {
  savedShot = null;
};

/** 撞く直前の状態へ全球を復元する（位置・状態を戻し静止させる）。戻り値: 復元したか。 */
export const restoreShotState = (table: Table): boolean => {
  if (!savedShot) return false;
  for (const s of savedShot) {
    const b = table.balls.find((x) => x.id === s.id);
    if (!b) continue;
    b.pos.set(s.x, s.y, s.z);
    b.vel.set(0, 0, 0);
    b.rvel.set(0, 0, 0);
    b.state = s.state;
  }
  return true;
};

// ---- スクワート（撞点ズレによる手球の飛び）＋ スワーブ補正（速度依存） ----
// 物理: スクワート＝english と逆へ撞き出し直後に偏向（角度はほぼ速度非依存）。
//       スワーブ＝english 側へ曲がり戻る（低速ほど大）。両者の合成で「ピボット現象」。
// 本実装(B)は両者を「初速進路の補正角」に集約した現象論モデル（水平キューでも速度依存が出る）。
const SQUIRT_K = 0.12; // トビ角係数 [rad]（offset.x=0.5, factor=1 で約3.4°）
const SWERVE_K = 0.2; // 低速で english 側へ戻す係数 [rad]
const SQUIRT_V_REF = 1.3; // スワーブが効く基準速度 [m/s]（ピボット速度の目安）

// 撞き出し初速の進路補正角 [rad]。符号は offset.x に依存（+で一方・-で他方）。
const squirtAngle = (offsetX: number, speed: number, factor: number): number => {
  if (factor <= 0 || offsetX === 0) return 0;
  const squirt = SQUIRT_K * factor * offsetX; // 速度非依存（トビ）
  const swerve =
    SWERVE_K * factor * offsetX * (SQUIRT_V_REF / (Math.max(0, speed) + SQUIRT_V_REF));
  // 速い→squirt 勝ち（english と逆）、遅い→swerve 勝ち（english 側）。
  return swerve - squirt;
};

/**
 * 手球の初速ベクトル vel を、撞点横ズレ offset.x・速度 speed・飛び量 factor[0..1] に応じて
 * z 軸まわりに回転（スクワート＋速度依存スワーブの合成）。cueStrike は非改変のまま後段補正。
 */
export const applySquirt = (
  vel: Vector3,
  offsetX: number,
  speed: number,
  factor: number,
): void => {
  const a = squirtAngle(offsetX, speed, factor);
  if (a === 0) return;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const vx = vel.x;
  const vy = vel.y;
  vel.x = vx * c - vy * s;
  vel.y = vx * s + vy * c;
};

/**
 * 撞点横ズレ offsetX・初速 speed・飛び量 factor による初速進路の偏向角 [rad]。
 * applySquirt と同じ符号・量で、実際の撞き出し方向 = aim.angle + この値 になる。
 * オートエイム（自動照準）はポケットIN進路角からこの値を引いて aim.angle を求める
 * （逆補正）。純粋な前方計算なので照準解決にそのまま流用できる。
 */
export const squirtDeflection = (
  offsetX: number,
  speed: number,
  factor: number,
): number => squirtAngle(offsetX, speed, factor);

/**
 * 接触距離 distance [m] でちょうど滑走中（スタン＝ストップショット相当）になる初速 [m/s]。
 * shotPrediction の滑走距離式 Ds = 12·v0²/(49·muS·g) を distance について解いたもの。
 * オートエイムの初期スナップ（強さ未確定時）の代表速度に使う（撞点偏向の見積り用）。
 */
export const stopShotSpeed = (distance: number): number =>
  Math.sqrt((Math.max(0, distance) * 49 * muS * g) / 12);

/**
 * 盤上の球の最大水平速度 [m/s]。落球アニメ中（Falling）は沈静化とみなさず Infinity を返す。
 * ゼロ速度付近の数値リミットサイクルで allStationary に収束しない場合の「近接静止」検出に使う。
 */
export const restActivity = (table: Table): number => {
  let maxSpeed = 0;
  for (const b of table.balls) {
    if (b.state === State.Falling) return Infinity; // 落球アニメ中は止めない
    if (!b.onTable()) continue;
    const s = Math.hypot(b.vel.x, b.vel.y);
    if (s > maxSpeed) maxSpeed = s;
  }
  return maxSpeed;
};

/**
 * ポケット捕捉円内に止まっている球を強制的にポケットイン（盤外）化する。
 * 強制静止や数値誤差で「落ちずにポケット内で静止」した球を確実に処理する。
 * 戻り値: 処理した球数。
 */
export const dropPottedBalls = (table: Table): number => {
  const centers = PocketGeometry.pocketCenters as Array<{
    pos: Vector3;
    radius: number;
  }>;
  let dropped = 0;
  for (const b of table.balls) {
    if (!b.onTable()) continue;
    for (const p of centers) {
      const dx = b.pos.x - p.pos.x;
      const dy = b.pos.y - p.pos.y;
      if (dx * dx + dy * dy < p.radius * p.radius) {
        b.vel.set(0, 0, 0);
        b.rvel.set(0, 0, 0);
        b.pos.z = -4 * R - (R * b.id) / 4; // 描画で隠れる深さ（restingDepth - R 相当）
        b.state = State.InPocket;
        dropped++;
        break;
      }
    }
  }
  return dropped;
};

/**
 * 指定 id の盤上球を（捕捉円判定なしで）強制的にポケットイン化する。
 * 遠隔の軌跡リプレイで「ポケットインした球（トラックが入口で途切れた球）」を、
 * 入口で停止させず確実に落球（盤外）させるために使う。位置はその場（入口付近）で z を沈める。
 */
export const forcePotBalls = (table: Table, ids: ReadonlySet<number>): void => {
  for (const b of table.balls) {
    if (!ids.has(b.id) || !b.onTable()) continue;
    b.vel.set(0, 0, 0);
    b.rvel.set(0, 0, 0);
    b.pos.z = -4 * R - (R * b.id) / 4; // dropPottedBalls と同じ深さ（描画で隠れる）
    b.state = State.InPocket;
  }
};

// ---- テーブルコンディション（湿度 → 摩擦） ----
// 湿度↑でクロス/クッションの摩擦が増し、転がりが重く・クッションの反射角がつまる。
// 反発係数（COR）は変更しない。
// 基準値（標準＝スライダー中央）は「従来の最重相当」に引き上げ（全体的に重い卓）。
// 標準より乾燥側で軽く（≈従来の標準の速さ）、湿潤側でさらに重くなる。
// 旧: BASE_MU=0.007（標準=1.0・最重≈0.0132）。現: 標準を旧最重≈0.0132 に置く。
const BASE_MU = 0.0132; // 転がり摩擦の標準（従来の最重相当）
const BASE_MUS = 0.212; // 滑り摩擦の標準（従来の最重相当）
// クッション摩擦は既定 0.85 を標準とする。1 近傍/超では Han クッションモデルが
// 不安定化（クッション際で微振動が止まらず allStationary に収束しない）ため、
// 標準＝既定値を維持し、スケールも 0.9 未満に厳しく収める。
const BASE_MUC = 0.85;

// h: 0=乾燥 / 0.5=中央(標準) / 1=最湿。3 点アンカー線形補間で BASE への倍率を算出する。
// 旧版は dry→1→humid の 2 点（中央 factor=1）だったが、ユーザが「もっと重い卓」を
// 要望したため、中央＝従来 max（≈1.25×）・最湿端＝さらに重い（≈1.7×）へシフトした。
const conditionFactor = (
  h: number,
  dry: number,
  mid: number,
  humid: number,
): number => {
  const t = Math.max(0, Math.min(1, h));
  return t <= 0.5
    ? dry + (mid - dry) * (t / 0.5)
    : mid + (humid - mid) * ((t - 0.5) / 0.5);
};

/**
 * 湿度 [0,1] からクロス（転がり/滑り）とクッションの摩擦係数を更新する。
 * 反発係数は不変。予測軌跡（simulateCuePath）にも同じ係数が反映される。
 *
 * 3 点マッピング: 乾燥端 → 中央(標準) → 湿潤端。中央は従来の最湿端（factor 1.25）相当に
 * 引き上げ、湿潤端をさらに 1.7 倍まで拡張＝「もっと重く」できるようになる。
 */
export const applyTableCondition = (humidity: number): void => {
  // mu  : dry 0.53（≈0.007 旧標準）/ mid 1.25 / humid 1.7（さらに重い）
  // muS : dry 0.64（≈0.136 旧標準）/ mid 1.25 / humid 1.7
  setmu(BASE_MU * conditionFactor(humidity, 0.53, 1.25, 1.7));
  setmuS(BASE_MUS * conditionFactor(humidity, 0.64, 1.25, 1.7));
  // muC は 0.85 を上限とする（0.85 超で Han クッションが微振動し収束が遅れるため、
  // 湿潤側でも増やさず据え置き、乾燥側のみ僅かに下げる）。反射角の湿度依存は見送り。
  setmuC(BASE_MUC * conditionFactor(humidity, 0.94, 1.0, 1.0));
};

// ---- ショット力インジケーター用の予測（Han 摩擦モデルの解析積分・センター撞き前提） ----

/** 手玉走行予測。 */
export interface ShotPrediction {
  /** 手玉が静止するまでの総走行距離 [m]（滑走 + 転がり）。 */
  readonly reach: number;
  /** 自然転がりに移るまでの滑走距離 [m]（衝突点がこれ以内なら衝突時に滑走中＝スタン）。 */
  readonly slideDist: number;
}

/**
 * 初速 v0 = power × cos(elevation)（cueStrike と同じ）。
 * 滑走: 線形減速 muS·g、自然転がりへ v_roll=(5/7)v0、滑走距離 Ds = 12·v0² / (49·muS·g)。
 * 転がり: 線形減速 a_roll = mu·g/√2（エンジンの rollingFull に一致）、Dr = v_roll² / (2·a_roll)。
 */
export const shotPrediction = (
  power: number,
  elevation: number,
): ShotPrediction => {
  const v0 = Math.max(0, power * Math.cos(elevation));
  const slideDist = (12 * v0 * v0) / (49 * muS * g);
  const vRoll = (5 / 7) * v0;
  const aRoll = (mu * g) / Math.SQRT2;
  const rollDist = aRoll > 0 ? (vRoll * vRoll) / (2 * aRoll) : 0;
  return { reach: slideDist + rollDist, slideDist };
};

/**
 * 手玉から angle 方向のレイで最初に接触する的玉までの距離 [m]（中心間 2R で接触）。
 * 手玉前方の的玉のみ対象。当たらなければ null。
 */
export const firstContactDistance = (
  table: Table,
  angle: number,
): number | null => {
  const cb = table.cueball;
  if (!cb.onTable()) return null;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const r2 = (2 * R) * (2 * R);
  let best: number | null = null;
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    const tx = b.pos.x - cb.pos.x;
    const ty = b.pos.y - cb.pos.y;
    const proj = tx * dx + ty * dy; // 前方への射影距離
    if (proj <= 0) continue; // 後方は対象外
    const perp2 = tx * tx + ty * ty - proj * proj;
    if (perp2 > r2) continue; // レイが球をかすめない
    const contact = proj - Math.sqrt(r2 - perp2);
    if (contact < 0) continue;
    if (best === null || contact < best) best = contact;
  }
  return best;
};

/** 照準線上で最初に当たる的玉の id（firstContactDistance と同条件）。なければ null。
 *  フォローカメラの「的球」確定に使う（撞いた瞬間に捕捉）。 */
export const aimedTargetId = (table: Table, angle: number): number | null => {
  const cb = table.cueball;
  if (!cb.onTable()) return null;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const r2 = 2 * R * (2 * R);
  let best: number | null = null;
  let bestId: number | null = null;
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    const tx = b.pos.x - cb.pos.x;
    const ty = b.pos.y - cb.pos.y;
    const proj = tx * dx + ty * dy; // 前方への射影距離
    if (proj <= 0) continue; // 後方は対象外
    const perp2 = tx * tx + ty * ty - proj * proj;
    if (perp2 > r2) continue; // レイが球をかすめない
    const contact = proj - Math.sqrt(r2 - perp2);
    if (contact < 0) continue;
    if (best === null || contact < best) {
      best = contact;
      bestId = b.id;
    }
  }
  return bestId;
};

/** フォローカメラの構図点。primary=必ず画面に収める対象 / secondary=余裕があれば収める対象。 */
export interface FollowPoint {
  readonly x: number;
  readonly y: number;
}
export interface FollowFrameData {
  readonly primary: FollowPoint[];
  readonly secondary: FollowPoint[];
}

const FOLLOW_MOVE_EPS = 0.02; // これ未満は静止扱い（フレーミング対象外）
const FOLLOW_POCKET_SPEED = 0.15; // ポケット方向と判定する最低速度 [m/s]
const FOLLOW_POCKET_COS = 0.92; // 速度ベクトルとポケット方向の一致度（cos≈23°以内）

/**
 * ショット中のフォローカメラ用に、画面へ収めるべき球を分類して返す。
 * - primary（必ず収める）: 手球(0) / 的球(targetBallId) / ポケットへ向かっている球
 * - secondary（余裕があれば収める）: それ以外の動いている球
 * 静止している無関係な球はフレーミング対象に含めない（引きすぎ防止）。
 */
export const followFrame = (
  table: Table,
  targetBallId: number | null,
): FollowFrameData => {
  const centers = PocketGeometry.pocketCenters as Array<{
    pos: Vector3;
    radius: number;
  }>;
  const primary: FollowPoint[] = [];
  const secondary: FollowPoint[] = [];
  const inPrimary = new Set<number>();

  const cue = table.cueball;
  if (cue.onTable()) {
    primary.push({ x: cue.pos.x, y: cue.pos.y });
    inPrimary.add(0);
  }
  if (targetBallId !== null) {
    const tb = table.balls.find((b) => b.id === targetBallId);
    if (tb && tb.onTable()) {
      primary.push({ x: tb.pos.x, y: tb.pos.y });
      inPrimary.add(tb.id);
    }
  }

  for (const b of table.balls) {
    if (!b.onTable() || inPrimary.has(b.id)) continue;
    const sp = Math.hypot(b.vel.x, b.vel.y);
    if (sp < FOLLOW_MOVE_EPS) continue; // 静止球は無視
    let pocketBound = false;
    if (sp >= FOLLOW_POCKET_SPEED) {
      const nvx = b.vel.x / sp;
      const nvy = b.vel.y / sp;
      for (const p of centers) {
        const dx = p.pos.x - b.pos.x;
        const dy = p.pos.y - b.pos.y;
        const d = Math.hypot(dx, dy);
        if (d < 1e-6) continue;
        if ((nvx * dx + nvy * dy) / d >= FOLLOW_POCKET_COS) {
          pocketBound = true; // ポケット方向へ十分揃って向かっている
          break;
        }
      }
    }
    if (pocketBound) primary.push({ x: b.pos.x, y: b.pos.y });
    else secondary.push({ x: b.pos.x, y: b.pos.y });
  }

  return { primary, secondary };
};

/** 描画用スナップショットを取得。 */
export const snapshot = (table: Table): BallSnapshot[] =>
  table.balls.map((b) => ({
    id: b.id,
    x: b.pos.x,
    y: b.pos.y,
    z: b.pos.z,
    wx: b.rvel.x,
    wy: b.rvel.y,
    wz: b.rvel.z,
    onTable: b.onTable(),
  }));

// ---- 遠隔ショットの軌跡プレイバック（端末間の物理非決定性を回避） ----
// 権威側から届いた位置へ球を移動し、移動量から転がり回転を導出して rvel に入れる
// （snapshot 経由で描画が自然に転がって見える）。物理は進めない（受信側は再生のみ）。

/** 送信用: 現在 on-table の全球の位置（id+x+y）。受信側が補間再生する。 */
export const onTablePositions = (
  table: Table,
): Array<{ id: number; x: number; y: number }> => {
  const out: Array<{ id: number; x: number; y: number }> = [];
  for (const b of table.balls) {
    if (b.onTable()) out.push({ id: b.id, x: b.pos.x, y: b.pos.y });
  }
  return out;
};

/** 受信側: 補間位置へ球を移動し、転がり回転（ω = ẑ×v / R）を rvel へ導出する。 */
export const applyPlaybackFrame = (
  table: Table,
  frame: ReadonlyMap<number, { x: number; y: number }>,
  dt: number,
): void => {
  for (const b of table.balls) {
    const p = frame.get(b.id);
    if (!p) continue;
    const dx = p.x - b.pos.x;
    const dy = p.y - b.pos.y;
    b.pos.set(p.x, p.y, b.pos.z);
    b.vel.set(0, 0, 0);
    if (dt > 0 && (dx !== 0 || dy !== 0)) {
      const vx = dx / dt;
      const vy = dy / dt;
      // 転がりなし滑りの拘束: 接地点速度ゼロ → ωx=-vy/R, ωy=vx/R。
      b.rvel.set(-vy / ballRadius, vx / ballRadius, 0);
    } else {
      b.rvel.set(0, 0, 0);
    }
  }
};

// ---- 永続化（レジューム）: 盤面の直列化／復元 ----
// GPL エンジン（Ball/State）への書き込みはこの型付き境界に限定する（封じ込め設計）。

/** 永続化用の球状態（位置 + 状態のみ。静止時保存前提のため速度は持たない）。 */
export interface PersistedBall {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** State enum の値（文字列。"Stationary" / "InPocket" 等）。 */
  readonly state: string;
}

/** 文字列が State enum のいずれかの値か判定し、該当すれば State を返す（なければ null）。 */
const STATE_VALUES: readonly State[] = [
  State.Stationary,
  State.Rolling,
  State.Sliding,
  State.Falling,
  State.InPocket,
];
const toState = (s: string): State | null =>
  STATE_VALUES.find((v) => v === s) ?? null;

/** 現在の全球状態を永続化用に直列化する（静止時に呼ぶ前提・速度は保存しない）。 */
export const serializeBalls = (table: Table): PersistedBall[] =>
  table.balls.map((b) => ({
    id: b.id,
    x: b.pos.x,
    y: b.pos.y,
    z: b.pos.z,
    state: b.state,
  }));

/**
 * 永続化データから盤面を復元する。現テーブルと保存データの id 構成が完全一致し、
 * かつ各 state が妥当な enum 値のときのみ位置・状態を上書きし、速度をゼロにして
 * 静止状態で復元する。構成不一致（モード差異・球数違い・破損・不正な状態値）の
 * 場合は盤面を一切変更せず false を返し、呼び出し側は新規ラックへフォールバックする。
 */
export const restoreBalls = (
  table: Table,
  balls: readonly PersistedBall[],
): boolean => {
  if (balls.length === 0 || balls.length !== table.balls.length) return false;
  const ids = new Set(table.balls.map((b) => b.id));
  // まず全要素を検証（部分適用で盤面が壊れるのを防ぐ）。
  const resolved: { ball: InstanceType<typeof Ball>; s: PersistedBall; state: State }[] = [];
  for (const s of balls) {
    if (!ids.has(s.id)) return false;
    const state = toState(s.state);
    if (state === null) return false;
    const ball = table.balls.find((x) => x.id === s.id);
    if (!ball) return false;
    resolved.push({ ball, s, state });
  }
  // 検証を全通過してから一括適用。
  for (const { ball, s, state } of resolved) {
    ball.pos.set(s.x, s.y, s.z);
    ball.vel.set(0, 0, 0);
    ball.rvel.set(0, 0, 0);
    ball.state = state;
  }
  return true;
};

// ---- ルールエンジン用 観測境界（物理 → ShotObservation 変換） ----
// GPL の Ball/Table はここでだけ読み、rules には平データ（ShotObservation）を渡す。
// 設計: knowledge/architecture/nineball-rules-engine.md §2。

/** 物理イベント（ball id 付き・効果音＋ルール観測の共通入力）。 */
export interface ShotOutcome {
  readonly type: 'collision' | 'cushion' | 'pot';
  readonly a: number;
  readonly b: number;
  readonly speed: number;
}

/** engine の outcome ログを id 付きで取り出す（消費＝肥大防止）。 */
export const drainShotOutcomes = (table: Table): ShotOutcome[] =>
  table.drainOutcomes().map((o) => ({
    type: o.type,
    a: o.a,
    b: o.b,
    speed: o.speed,
  }));

/** ショット開始時のスナップショット（観測の基準）。 */
export interface PreShot {
  readonly targetBallId: number | null;
  readonly onTableIds: readonly number[];
}

/** 打前の基準（最小番号球・盤上の球 id 集合）を取得する。 */
export const beginShotObservation = (table: Table): PreShot => ({
  targetBallId: lowestOnTableBallId(table),
  onTableIds: table.balls.filter((b) => b.onTable()).map((b) => b.id),
});

/**
 * 打後にショット観測を確定する。
 * - firstContactId: outcome 順で最初の「手球(0)↔的玉」collision の的玉 id。
 * - railAfterContact: その正接触以降に cushion イベントがあったか。
 * - railedNumberedBalls: cushion を起こした的玉 id 集合（ブレイク 4 球レール用）。
 * - pottedIds: 打前後の onTable 差分（手球 0 は除く）。
 * - doubleHitSuspected: 打前幾何で確定した値（呼び出し側が渡す・§8.1）。
 */
export const endShotObservation = (
  table: Table,
  pre: PreShot,
  outcomes: readonly ShotOutcome[],
  doubleHitSuspected: boolean,
  headStringIds: ReadonlySet<number> = new Set(),
): ShotObservation => {
  let firstContactId: number | null = null;
  let firstContactIdx = -1;
  for (let i = 0; i < outcomes.length; i++) {
    const o = outcomes[i]!;
    if (o.type !== 'collision') continue;
    const otherId = o.a === 0 ? o.b : o.b === 0 ? o.a : -1;
    if (otherId > 0) {
      firstContactId = otherId;
      firstContactIdx = i;
      break;
    }
  }

  let railAfterContact = false;
  if (firstContactIdx >= 0) {
    for (let i = firstContactIdx + 1; i < outcomes.length; i++) {
      if (outcomes[i]!.type === 'cushion') {
        railAfterContact = true;
        break;
      }
    }
  }

  const railed = new Set<number>();
  for (const o of outcomes) {
    if (o.type === 'cushion' && o.a > 0) railed.add(o.a);
  }

  const nowOn = new Set(
    table.balls.filter((b) => b.onTable()).map((b) => b.id),
  );
  const pottedIds = pre.onTableIds.filter((id) => id !== 0 && !nowOn.has(id));

  // ヘッドストリングを越えた的玉のうち、落球していない（盤上に残った/戻った）本数。
  // 3ポイントルールの判定用（落球はポケット分として別カウントするため除外）。
  const pottedSet = new Set(pottedIds);
  let headStringCrossings = 0;
  for (const id of headStringIds) {
    if (id !== 0 && !pottedSet.has(id)) headStringCrossings++;
  }

  return {
    targetBallId: pre.targetBallId,
    firstContactId,
    pottedIds,
    cueScratched: !table.cueball.onTable(),
    railAfterContact,
    railedNumberedBalls: [...railed],
    headStringCrossings,
    objectBallOffTable: false, // 近似（本エンジンはクッションでクランプ・§8）
    doubleHitSuspected,
  };
};

/** 二度撞きプロキシのしきい値（設計 §8.1）。 */
export interface DoubleHitGeom {
  readonly gapMaxM: number;
  readonly elevationMaxRad: number;
  readonly cutAngleMaxRad: number;
}

/**
 * 二度撞き 近接球プロキシ（打前幾何）。3 条件の論理積で true（設計 §8.1）:
 *   隙間 ≤ gapMaxM ∧ 仰角 ≤ elevationMaxRad ∧ カット角 ≤ cutAngleMaxRad。
 * 対象は照準線上で最初に当たる的玉（firstContactDistance と同じ判定）。
 */
export const detectDoubleHitGeometry = (
  table: Table,
  aimAngle: number,
  elevation: number,
  params: DoubleHitGeom,
): boolean => {
  if (elevation > params.elevationMaxRad) return false;
  const cb = table.cueball;
  if (!cb.onTable()) return false;
  const dx = Math.cos(aimAngle);
  const dy = Math.sin(aimAngle);
  const r2 = (2 * R) * (2 * R);
  let bestGap = Infinity;
  let bestCx = 0;
  let bestCy = 0;
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    const tx = b.pos.x - cb.pos.x;
    const ty = b.pos.y - cb.pos.y;
    const proj = tx * dx + ty * dy;
    if (proj <= 0) continue;
    const perp2 = tx * tx + ty * ty - proj * proj;
    if (perp2 > r2) continue;
    const contact = proj - Math.sqrt(r2 - perp2); // 表面が触れるまでの距離（≈隙間）
    if (contact < 0) continue;
    if (contact < bestGap) {
      bestGap = contact;
      bestCx = b.pos.x;
      bestCy = b.pos.y;
    }
  }
  if (!Number.isFinite(bestGap) || bestGap > params.gapMaxM) return false;
  // カット角 = 照準方向と「手球中心→的玉中心」の成す角。
  const ux = bestCx - cb.pos.x;
  const uy = bestCy - cb.pos.y;
  const ulen = Math.hypot(ux, uy);
  if (ulen < 1e-9) return true; // ほぼ重なり＝正面とみなす
  const cosA = (ux * dx + uy * dy) / ulen;
  const cut = Math.acos(Math.max(-1, Math.min(1, cosA)));
  return cut <= params.cutAngleMaxRad;
};

/**
 * 指定球をフットスポット（占有時は後方ロングストリング上の空き）へ再設置する。
 * 9 番のスポット用。速度ゼロ・静止状態で盤上に戻す。
 */
export const spotBall = (table: Table, id: number): void => {
  const b = table.balls.find((x) => x.id === id);
  if (!b) return;
  const foot = footSpot();
  const place = (x: number, y: number): void => {
    b.pos.set(x, y, 0);
    b.vel.set(0, 0, 0);
    b.rvel.set(0, 0, 0);
    b.state = State.Stationary;
  };
  // フットスポット → 後方（フットレール側 +x）へ 2R 刻みで空きを探す。
  if (isFreeSpot(table, foot.x, foot.y, id)) {
    place(foot.x, foot.y);
    return;
  }
  for (let i = 1; i <= 24; i++) {
    const x = foot.x + i * 2 * R;
    if (x > TableGeometry.tableX - R) break;
    if (isFreeSpot(table, x, foot.y, id)) {
      place(x, foot.y);
      return;
    }
  }
  // フォールバック: フットスポットへ強制配置。
  place(foot.x, foot.y);
};

/** 自動仰角の上限 [rad]（60°）。これ以上はジャンプ/massé領域なので入力UI側に委ねる。 */
export const MAX_AUTO_ELEVATION = Math.PI / 3;

// キュー中心線が後方レール上面を越えるのに要するクリアランス高さ [m]。
const RAIL_CLEARANCE_Z = R;
// レールクリア用の自動仰角の上限 [rad]（9°）。レール際でもキューはほぼ寝かせたまま
// （steep にしない）。届かない下撞点は computeRailMinOffsetY が「上だけ」に制限する。
const RAIL_CLEAR_MAX_ELEVATION = Math.PI / 20;

/** 手球後方（ショット反対方向）に外周レールへ達するまでの距離。なければ Infinity。 */
const backRailDistance = (table: Table, aimAngle: number): number => {
  const cb = table.cueball.pos;
  const halfX = TableGeometry.tableX;
  const halfY = TableGeometry.tableY;
  const dx = -Math.cos(aimAngle);
  const dy = -Math.sin(aimAngle);
  const tx = dx === 0 ? Infinity : ((dx > 0 ? halfX : -halfX) - cb.x) / dx;
  const ty = dy === 0 ? Infinity : ((dy > 0 ? halfY : -halfY) - cb.y) / dy;
  return Math.min(tx > 0 ? tx : Infinity, ty > 0 ? ty : Infinity);
};

/**
 * キュー手元側が後方レール上面にめり込まないための最小仰角を返す。
 *
 * 接触点の高さ contactZ = offsetY*2R から、距離 tRail で z>=RAIL_CLEARANCE_Z を満たす
 * 角 atan((clear-contactZ)/tRail)。レールから離れるほど僅かな仰角で済む（=寝かせたまま）。
 * **上限 RAIL_CLEAR_MAX_ELEVATION でキャップ**＝レール際でも steep にせず寝かせる。
 * 届かない下撞点は撞点側（computeRailMinOffsetY）で「上だけ」に制限される。
 */
export const computeRailClearanceElevation = (
  table: Table,
  aimAngle: number,
  cueLength: number,
  offsetY: number,
  maxElevation: number = RAIL_CLEAR_MAX_ELEVATION,
): number => {
  const tRail = backRailDistance(table, aimAngle);
  // 後方レールがキューの届く範囲（cueLength）にある時だけクリアが必要。
  if (tRail <= 0 || tRail >= cueLength) return 0;
  const contactZ = offsetY * 2 * R;
  const needed = Math.atan(Math.max(0, RAIL_CLEARANCE_Z - contactZ) / tRail);
  return Math.min(needed, maxElevation);
};

/**
 * 手球後方にレールが近接している時の、撞点 offset.y の下限。
 *
 * 現仰角 elevation のキューが後方レール上面（RAIL_CLEARANCE_Z）を越えて届く最低接触高さ
 *   minContactZ = RAIL_CLEARANCE_Z - tRail*tan(elevation)
 * を offset.y へ換算したもの。レール際ではキューを寝かせる（小仰角）ため minContactZ が高く
 * = **上側の撞点しか届かない**。レールから離れるほど（tRail 大）/ 仰角を上げるほど下も届く。
 * computeRailClearanceElevation と同一前提なので、両者は矛盾なく一致する。
 *
 * 返り値 [-0.5, 0.5]: -0.5 = 制限なし。
 */
export const computeRailMinOffsetY = (
  table: Table,
  aimAngle: number,
  cueLength: number,
  elevation: number,
): number => {
  const tRail = backRailDistance(table, aimAngle);
  if (tRail <= 0 || tRail >= cueLength) return -0.5;
  const minContactZ = RAIL_CLEARANCE_Z - tRail * Math.tan(elevation);
  return Math.max(-0.5, Math.min(0.5, minContactZ / (2 * R)));
};

/** 撞点パッド空間（offset 単位 [-0.5,0.5]）で「撞けない」円領域。 */
export interface SpinForbiddenDisk {
  cx: number; // 円中心 offset.x
  cy: number; // 円中心 offset.y
  r: number; // 半径（offset 単位）
}

// キューシャフト半径（手元 butt 〜 先 tip）。cuemesh の createCue 引数に対応:
//   tip = R*0.07/0.5 = R*0.14 / butt = R*0.23/0.5 = R*0.46。
const CUE_TIP_RADIUS = R * 0.14;
const CUE_BUTT_RADIUS = R * 0.46;

/**
 * 後方に近接した他球による撞点の禁止領域を「円のリスト」で返す。
 *
 * 旧実装は offset.y の下限（下方全幅マスク）だったため、球が少しでも掛かると
 * 横位置に関係なく下半分が一律で制限された。本実装は、キュー先端の接触点
 * (offset.x, offset.y) からショット反対方向＋仰角へ伸びるシャフトが、後方球と
 * 実際に重なる範囲だけを禁止円とする（重なっていない撞点は自由）。
 *
 * 幾何（接触平面 = 接触点を通りシャフト方向 u に垂直な面への射影）:
 *   - 側方軸 vCue = (-sinθ, cosθ) … offset.x に対応
 *   - 縦軸 = world +z … offset.y に対応
 *   後方球中心 e=(ex,ey,0) の射影は side = e·vCue, up = -sinε·proj。
 *   接触点 (ox,oy) は接触平面上で (ox·2R, oy·2R)。両者の距離 < (R + シャフト半径)
 *   で衝突 → 禁止円 中心(side/2R, -sinε·proj/2R)・半径(R+シャフト半径)/2R。
 *
 * 仰角 elevation を上げると禁止円が下方へ移動し、上側・中心の撞点が解放される。
 */
export const computeSpinForbiddenDisks = (
  table: Table,
  aimAngle: number,
  cueLength: number,
  elevation: number,
): SpinForbiddenDisk[] => {
  const cb = table.cueball.pos;
  const ca = Math.cos(aimAngle);
  const sa = Math.sin(aimAngle);
  const se = Math.sin(elevation);

  const disks: SpinForbiddenDisk[] = [];
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    const ex = b.pos.x - cb.x;
    const ey = b.pos.y - cb.y;
    // 後方（ショット反対方向）への距離。前方/遠すぎる球は対象外。
    const proj = -(ex * ca + ey * sa);
    if (proj <= 0 || proj > cueLength) continue;
    // 側方成分（offset.x 軸 = (-sinθ, cosθ)）。
    const side = ex * -sa + ey * ca;
    // proj に応じたシャフト半径（先端→手元で太くなる）。
    const shaftR =
      CUE_TIP_RADIUS +
      (CUE_BUTT_RADIUS - CUE_TIP_RADIUS) * Math.min(1, proj / cueLength);
    const r = (R + shaftR) / (2 * R);
    const cx = side / (2 * R);
    const cy = (-se * proj) / (2 * R);
    // パッド（半径 0.5 の円）と交差しない禁止円は無視。
    if (Math.hypot(cx, cy) - r > 0.5) continue;
    disks.push({ cx, cy, r });
  }
  return disks;
};

/** 軌跡の点（テーブル平面 x,y）。 */
export interface PathPoint {
  readonly x: number;
  readonly y: number;
}

// 軌跡シミュレーション用サンドボックス（手球1個のみ）。毎回 new せず再利用する。
let sandboxBall: Ball | null = null;
let sandboxTable: Table | null = null;

export interface CuePathOptions {
  readonly dt?: number; // 積分ステップ [s]
  readonly maxSteps?: number; // 計算打ち切りステップ数
  readonly sampleDist?: number; // 点を追加する移動距離間隔 [m]
  readonly maxPoints?: number; // 点列の上限（=実効的な描画長の上限）
  readonly squirt?: number; // 飛び量 factor[0..1]（撞点ズレによる初速偏向）
}

/**
 * 手球の実軌跡を**物理エンジンでシミュレート**して点列で返す。
 * cueStrike(angle, power, offset, elevation) で初速・スピンを与え、手球1個の
 * サンドボックス Table を advance() で進める。クッション反射（スピンによるスロー/
 * English）・仰角によるカーブ・転がり減速・到達距離がエンジン由来で正確に反映される。
 * 最初の的玉接触（実テーブルの球と中心間 2R）または静止/落下で停止。
 */
export const simulateCuePath = (
  table: Table,
  angle: number,
  power: number,
  offset: Vector3,
  elevation: number,
  options?: CuePathOptions,
): PathPoint[] => {
  const cb = table.cueball;
  const pts: PathPoint[] = [];
  if (!cb.onTable() || power <= 0) return pts;

  const dt = options?.dt ?? 1 / 240;
  const maxSteps = options?.maxSteps ?? 3000;
  const sampleDist = options?.sampleDist ?? R;
  const maxPoints = options?.maxPoints ?? 400;

  if (!sandboxBall || !sandboxTable) {
    sandboxBall = new Ball(new Vector3(0, 0, 0));
    sandboxTable = new Table([sandboxBall]);
  }
  const ball = sandboxBall;
  const tbl = sandboxTable;
  tbl.outcome.length = 0; // 蓄積する衝突ログをクリア

  ball.pos.copy(cb.pos);
  const strike = cueStrike(angle, power, offset, elevation);
  ball.vel.copy(strike.vel);
  ball.rvel.copy(strike.rvel);
  // スクワート（飛び）を初速に反映（実ショットと同じ補正）。
  applySquirt(ball.vel, offset.x, ball.vel.length(), options?.squirt ?? 0);
  ball.state = State.Sliding;

  // 的玉（実テーブルの手球以外・盤上）。軌跡はこれに当たったら止める。
  const targets = table.balls.filter((b) => b.id !== 0 && b.onTable());
  const r2 = (2 * R) * (2 * R);

  pts.push({ x: ball.pos.x, y: ball.pos.y });
  let lastX = ball.pos.x;
  let lastY = ball.pos.y;

  for (let step = 0; step < maxSteps && pts.length < maxPoints; step++) {
    // 予測軌跡シミュレーション中の advance() は、強パワー（カーボンキュー等）×密集球で
    // 衝突解決が深さ 100 を超えると throw する。プレビューは「途中まで描画 OK・赤バナーで
    // ゲームを止めない」が正しい挙動なので例外を捕まえてループを抜ける。
    try {
      tbl.advance(dt);
    } catch {
      break;
    }

    // 的玉接触で停止。
    let hit = false;
    for (const b of targets) {
      const ddx = ball.pos.x - b.pos.x;
      const ddy = ball.pos.y - b.pos.y;
      if (ddx * ddx + ddy * ddy <= r2) {
        hit = true;
        break;
      }
    }
    if (hit) {
      pts.push({ x: ball.pos.x, y: ball.pos.y });
      break;
    }

    // 一定距離ごとにサンプリング（カーブ・クッションを点列で表現）。
    if (Math.hypot(ball.pos.x - lastX, ball.pos.y - lastY) >= sampleDist) {
      pts.push({ x: ball.pos.x, y: ball.pos.y });
      lastX = ball.pos.x;
      lastY = ball.pos.y;
    }

    // 静止または落下で終了（最終位置を追加）。
    if (!ball.inMotion() || !ball.onTable()) {
      const last = pts[pts.length - 1]!;
      if (last.x !== ball.pos.x || last.y !== ball.pos.y) {
        pts.push({ x: ball.pos.x, y: ball.pos.y });
      }
      break;
    }
  }
  return pts;
};

// 的玉方向シミュレーション用サンドボックス（手球＋的玉の2球）。毎回 new せず再利用する。
let aimSandbox: { table: Table; cue: Ball; obj: Ball } | null = null;

/**
 * 手球を (aimAngle, power, offset, elevation, squirtFactor) で撞いた時、的玉 targetId が
 * **実際に出ていく方向** [rad] を物理エンジンでシミュレートして返す（スクワート・スワーブ・
 * 衝突スロー＝英式回転による投げを全て反映）。手球と的玉だけのサンドボックスで衝突直後の
 * 的玉速度方向を読む。手球が的玉に当たらない／power<=0 等は null。
 * オートエイムはこれを使い、的玉方向がポケットへ向くよう aim を数値逆算する（解析式の限界回避）。
 */
export const simulateObjectBallDir = (
  table: Table,
  targetId: number,
  aimAngle: number,
  power: number,
  offset: Vector3,
  elevation: number,
  squirtFactor: number,
): number | null => {
  const cb = table.cueball;
  const tb = table.balls.find((b) => b.id === targetId);
  if (!cb.onTable() || !tb || !tb.onTable() || power <= 0) return null;

  if (!aimSandbox) {
    const cue = new Ball(new Vector3(0, 0, 0));
    const obj = new Ball(new Vector3(1, 0, 0));
    aimSandbox = { table: new Table([cue, obj]), cue, obj };
  }
  const { table: tbl, cue, obj } = aimSandbox;
  tbl.outcome.length = 0;
  cue.pos.copy(cb.pos);
  cue.vel.set(0, 0, 0);
  cue.rvel.set(0, 0, 0);
  cue.state = State.Stationary;
  obj.pos.copy(tb.pos);
  obj.vel.set(0, 0, 0);
  obj.rvel.set(0, 0, 0);
  obj.state = State.Stationary;

  const strike = cueStrike(aimAngle, power, offset, elevation);
  cue.vel.copy(strike.vel);
  cue.rvel.copy(strike.rvel);
  // 実ショットと同じスクワート（飛び）を初速へ反映。
  applySquirt(cue.vel, offset.x, cue.vel.length(), squirtFactor);
  cue.state = State.Sliding;

  const dt = 1 / 240;
  const twoR = 2 * R;
  let contacted = false;
  for (let i = 0; i < 2400; i++) {
    // 的玉方向サンドボックスの advance() も、強パワーや退化幾何で「Depth exceeded」を
    // 投げ得る。オートエイム途中での例外伝播を避けるため、break して既存の post-loop
    // フォールバック（contacted ? atan2 : null）に処理を委ねる。
    try {
      tbl.advance(dt);
    } catch {
      break;
    }
    const objSpeed = Math.hypot(obj.vel.x, obj.vel.y);
    if (!contacted && objSpeed > 1e-4) contacted = true;
    if (contacted) {
      // 手球と的玉が離れた（スロー確定）か、的玉が壁/ナックルに触れる前に方向を読む。
      const d = Math.hypot(cue.pos.x - obj.pos.x, cue.pos.y - obj.pos.y);
      if (d > twoR * 1.06 || !cue.inMotion()) {
        return Math.atan2(obj.vel.y, obj.vel.x);
      }
    }
    if (!cue.inMotion() && !obj.inMotion()) break;
  }
  return contacted ? Math.atan2(obj.vel.y, obj.vel.x) : null;
};

/** N 球フルシミュレーション結果（CPU 思考で使う実物理検証）。 */
export interface FullSimResult {
  /** 落球した番号球の id 配列（手球除く・順不問）。 */
  readonly pocketedIds: readonly number[];
  /** 手球がスクラッチ（ポケット入り）したか。 */
  readonly cueScratched: boolean;
  /** 手球が最初に衝突した「球」の id（球-球衝突のみ・クッション/ナックル除外）。null=何にも当たらなかった。 */
  readonly firstHitId: number | null;
  /** 手球の最終停止位置（スクラッチ時は落下前の最後位置）。 */
  readonly cueStopX: number;
  readonly cueStopY: number;
  /** タイムアウトせず静止に到達したか。 */
  readonly settled: boolean;
  /** クッションに当たった的玉 id（球-クッション衝突）。ブレイク合法/ノーレール判定用。 */
  readonly railedNumberedIds: readonly number[];
  /** 手球がクッションに接触したか（球-クッション衝突・no-rail foul 判定用）。 */
  readonly cueRailed: boolean;
  /** 手球が「最初に球と衝突する前」にクッションへ触れたか（≒キック/バンク軌道判定用）。
   *  true の場合「自然な直撃」ではなく、AI 思考の品質評価で減点する用途に使う。 */
  readonly cueRailedBeforeFirstHit: boolean;
  /** カロム（球-球衝突で手玉を含まないもの）に参加した球 id 一覧。
   *  例: 手玉→A→B のとき A と B が caromedIds に入る。
   *  AI 思考が「球同士をぶつけて入れる」トリックショットを通常ポットと区別するために使う。 */
  readonly caromedIds: readonly number[];
  /** 「手玉が最初に球と衝突した後」にいずれかの球がクッションに接触したか。
   *  9 ボール no-rail foul 判定（target hit 後にどの球もレールを取らずポケットインもしない = ファウル）に使う。 */
  readonly anyBallRailedAfterFirstHit: boolean;
}

/**
 * 現在の盤面に対し ShotParams を適用して**静止まで物理を進め**、結果を返す。
 * 状態は事前にスナップショット→シミュレーション→**完全復元**するため、呼び出し側の
 * 盤面は変更されない（CPU 思考で大量に呼ばれる前提）。
 *
 * 注意:
 *   - 静止時にのみ呼ぶこと（呼び出し時点で動球があると shoot が false を返し結果が無効）。
 *   - 物理エンジンの outcome ログは事前/事後にクリアされる（呼び出し側へ持ち越さない）。
 *   - 静止判定タイムアウト時は `forceSettle` で安全停止し、`settled=false` を返す。
 */
export const simulateFullShot = (table: Table, params: ShotParams): FullSimResult => {
  const SIM_DT = 1 / 240;
  const SIM_MAX_STEPS = 4000; // ~16.7 秒のシミュレーション上限

  // 状態スナップショット（位置+state のみ。静止時呼び出し前提のため velocity は不要）。
  const snapshot = serializeBalls(table);
  // 既存の outcome ログをクリア（汚染防止）。
  table.drainOutcomes();

  // 失敗パス: 既に動球がある → 復元のみ。
  if (!shoot(table, params)) {
    restoreBalls(table, snapshot);
    table.drainOutcomes();
    return {
      pocketedIds: [],
      cueScratched: false,
      firstHitId: null,
      cueStopX: 0,
      cueStopY: 0,
      settled: false,
      railedNumberedIds: [],
      cueRailed: false,
      cueRailedBeforeFirstHit: false,
      caromedIds: [],
      anyBallRailedAfterFirstHit: false,
    };
  }

  // 物理を静止まで進めながらイベントを集約。
  let firstHitId: number | null = null;
  const railedSet = new Set<number>();
  const caromedSet = new Set<number>();
  let cueRailed = false;
  let cueRailedBeforeFirstHit = false;
  let anyBallRailedAfterFirstHit = false;
  let steps = 0;
  let settled = false;
  while (steps < SIM_MAX_STEPS) {
    if (isResting(table)) {
      settled = true;
      break;
    }
    try {
      table.advance(SIM_DT);
    } catch {
      break;
    }
    const outs = table.drainOutcomes();
    for (const o of outs) {
      if (firstHitId === null && o.type === 'collision' && (o.a === 0 || o.b === 0)) {
        firstHitId = o.a === 0 ? o.b : o.a;
      }
      // カロム検出: 手玉(id=0)を含まない球-球衝突 = 番号球同士の衝突。
      // 「球をぶつけて入れる」トリックショット判定用。
      if (o.type === 'collision' && o.a !== 0 && o.b !== 0) {
        caromedSet.add(o.a);
        caromedSet.add(o.b);
      }
      if (o.type === 'cushion') {
        // a または b にいずれかの球 id が入る。
        // 手球(id=0) は cueRailed フラグへ・的玉(id≠0) は railedSet へ振り分ける（no-rail foul 判定用）。
        if (o.a === 0 || o.b === 0) {
          cueRailed = true;
          // 「最初に球と衝突する前」のクッション接触を記録（キック/バンク軌道判定用）。
          if (firstHitId === null) cueRailedBeforeFirstHit = true;
        } else {
          const id = o.a !== 0 ? o.a : o.b;
          railedSet.add(id);
        }
        // 9 ボール no-rail foul 判定: 「最初に球と衝突した後」のいずれかの球（手玉含む）のクッション接触。
        // firstHitId !== null = 既に target に当たっている → 以降の cushion が anyBallRailedAfterFirstHit を立てる。
        if (firstHitId !== null) {
          anyBallRailedAfterFirstHit = true;
        }
      }
    }
    steps++;
  }
  if (!isResting(table)) forceSettle(table);
  dropPottedBalls(table); // ポケット入口で停止した球を確実に沈める

  // 結果取得。
  const cueBall = table.balls.find((b) => b.id === 0)!;
  const cueScratched = !cueBall.onTable();
  const cueStopX = cueBall.pos.x;
  const cueStopY = cueBall.pos.y;
  // 落球: 撞前 Stationary だった番号球が撞後 onTable でない＝落ちた。
  const beforeOnTable = new Set(
    snapshot.filter((b) => b.state === State.Stationary).map((b) => b.id),
  );
  const afterOnTable = new Set(
    table.balls.filter((b) => b.onTable()).map((b) => b.id),
  );
  const pocketedIds: number[] = [];
  for (const id of beforeOnTable) {
    if (id === 0) continue;
    if (!afterOnTable.has(id)) pocketedIds.push(id);
  }

  // 状態復元。
  restoreBalls(table, snapshot);
  table.drainOutcomes(); // シミュ残響を残さない

  return {
    pocketedIds,
    cueScratched,
    firstHitId,
    cueStopX,
    cueStopY,
    settled,
    railedNumberedIds: Array.from(railedSet),
    cueRailed,
    cueRailedBeforeFirstHit,
    caromedIds: Array.from(caromedSet),
    anyBallRailedAfterFirstHit,
  };
};

/** 盤上に残っている球のうち id が最小のものを返す。なければ null。 */
export const lowestOnTableBallId = (table: Table): number | null => {
  let lowest: number | null = null;
  for (const b of table.balls) {
    if (b.id === 0 || !b.onTable()) continue;
    if (lowest === null || b.id < lowest) lowest = b.id;
  }
  return lowest;
};

/** 指定 id の球の x,y 座標。盤上にいなければ null。 */
export const ballPositionById = (
  table: Table,
  id: number,
): { x: number; y: number } | null => {
  const b = table.balls.find((x) => x.id === id);
  if (!b || !b.onTable()) return null;
  return { x: b.pos.x, y: b.pos.y };
};
